import React, { useMemo, useState, useCallback } from 'react';
import { useFlexibleList, useUpdateReferenceDocumentSilent } from '../../hooks/useSWR';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Input, Button } from '../../components/ui';
import { PencilIcon, CheckIcon, XIcon } from '../../components/ui/Icons';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function truncate(text, max = 120) {
  if (!text) return '—';
  const s = String(text);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/**
 * Read-only list with inline edit only on referenceDocument.
 */
const ReferenceDocumentsTabTable = ({
  endpoint,
  getDate,
  getDescription,
  searchPlaceholder = 'Search by code or description…',
}) => {
  const { theme } = useTheme();
  const { data: rows = [], isLoading, isError, mutate } = useFlexibleList(endpoint);
  const updateReferenceDocument = useUpdateReferenceDocumentSilent(endpoint);

  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [saveError, setSaveError] = useState('');

  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = !q
      ? [...list]
      : list.filter((row) => {
          const code = String(row.code || '').toLowerCase();
          const desc = String(getDescription(row) || '').toLowerCase();
          const ref = String(row.referenceDocument || '').toLowerCase();
          return code.includes(q) || desc.includes(q) || ref.includes(q);
        });

    const toTime = (row) => {
      const raw = typeof getDate === 'function' ? getDate(row) : null;
      if (!raw) return 0;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    matched.sort((a, b) => toTime(b) - toTime(a));
    return matched;
  }, [list, search, getDescription, getDate]);

  const startEdit = useCallback((row) => {
    setEditingId(row.id);
    setDraft(row.referenceDocument || '');
    setSaveError('');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setDraft('');
    setSaveError('');
  }, []);

  const saveEdit = useCallback(
    async (row) => {
      setSavingId(row.id);
      setSaveError('');
      try {
        const nextValue = draft.trim() ? draft.trim() : null;
        await updateReferenceDocument(row.id, nextValue);
        await mutate();
        setEditingId(null);
        setDraft('');
      } catch (err) {
        setSaveError(err?.message || 'Failed to save reference document');
      } finally {
        setSavingId(null);
      }
    },
    [draft, mutate, updateReferenceDocument],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn('py-8 text-center text-sm', theme.text.secondary)}>
        Failed to load records: {isError.message || 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 min-h-0 flex-1">
      <div className="flex items-center gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="max-w-md"
        />
        <span className={cn('text-xs', theme.text.muted)}>
          {filtered.length} record{filtered.length === 1 ? '' : 's'}
        </span>
      </div>

      {saveError && (
        <div className="text-sm text-red-600 dark:text-red-400">{saveError}</div>
      )}

      <div
        className={cn(
          'flex-1 min-h-0 overflow-auto rounded-lg border',
          theme.border.light,
        )}
      >
        <table className="w-full text-sm">
          <thead
            className={cn(
              'sticky top-0 z-10 border-b',
              theme.border.light,
              theme.colors.surface,
            )}
          >
            <tr className={cn(theme.text.secondary)}>
              <th className="text-left font-medium px-3 py-2.5 w-[140px]">Code</th>
              <th className="text-left font-medium px-3 py-2.5 w-[110px]">Date</th>
              <th className="text-left font-medium px-3 py-2.5">Description</th>
              <th className="text-left font-medium px-3 py-2.5 min-w-[240px]">
                Reference Document
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className={cn('px-3 py-10 text-center', theme.text.muted)}
                >
                  No records found
                </td>
              </tr>
            ) : (
              filtered.map((row) => {
                const isEditing = editingId === row.id;
                const isSaving = savingId === row.id;
                return (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-t align-top',
                      theme.border.subtle,
                      'hover:bg-slate-50/80 dark:hover:bg-slate-900/30',
                    )}
                  >
                    <td className={cn('px-3 py-2.5 font-medium whitespace-nowrap', theme.text.primary)}>
                      {row.code || '—'}
                    </td>
                    <td className={cn('px-3 py-2.5 whitespace-nowrap', theme.text.secondary)}>
                      {formatDate(getDate(row))}
                    </td>
                    <td className={cn('px-3 py-2.5', theme.text.primary)}>
                      {truncate(getDescription(row))}
                    </td>
                    <td className="px-3 py-2.5">
                      {isEditing ? (
                        <div className="flex items-start gap-2">
                          <Input
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder="e.g. EN01-ΓΔ.13/08 - 2.0"
                            disabled={isSaving}
                            className="flex-1"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveEdit(row);
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEdit();
                              }
                            }}
                            autoFocus
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => saveEdit(row)}
                            disabled={isSaving}
                            title="Save"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={cancelEdit}
                            disabled={isSaving}
                            title="Cancel"
                          >
                            <XIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 group">
                          <span
                            className={cn(
                              'flex-1 break-words',
                              row.referenceDocument
                                ? theme.text.primary
                                : theme.text.muted,
                            )}
                          >
                            {row.referenceDocument || '—'}
                          </span>
                          <button
                            type="button"
                            onClick={() => startEdit(row)}
                            className={cn(
                              'p-1.5 rounded opacity-70 hover:opacity-100 transition-opacity',
                              theme.text.secondary,
                              'hover:bg-slate-100 dark:hover:bg-slate-800',
                            )}
                            title="Edit reference document"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReferenceDocumentsTabTable;
